<template>
  <div
    ref="root"
    class="mx-auto px-4 py-1.5"
    :data-timestamp="event.timestamp"
  >
    <div class="flex flex-col items-center gap-3 rounded-8 bg-bg-base-bg4 shadow-raised px-4 py-3 w-fit mx-auto">
      <div class="flex flex-col items-center text-center">
        <p class="text-body-r-14-20 text-fg-base-fg10">
          {{ timeRange }}
          {{ statusText }}
        </p>
        <p
          v-if="displayAppointmentName"
          class="text-heading-sb-14-20 text-fg-base-fg10 text-center"
          data-test="appointment-name"
        >
          «{{ displayAppointmentName }}»
        </p>
      </div>
      <div class="flex justify-center gap-2 flex-wrap">
        <wui-btn
          variant="neut"
          state="tonal"
          :size="32"
          data-test="appointment-details-btn"
          @click="onDetailsClick"
        >
          {{ $t('chat.callDetails') }}
        </wui-btn>
        <wui-btn
          v-if="callRecordEvents.length"
          variant="neut"
          state="tonal"
          :size="32"
          data-test="appointment-download-btn"
          @click="onDownloadRecords"
        >
          {{ downloadRecordsLabel }}
        </wui-btn>
      </div>
    </div>
    <div
      v-if="isUnread"
      class="relative"
    >
      <span class="absolute right-[-10px] bottom-0 h-2 w-2 rounded-full bg-primary-tints-400" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn } from '@wui/common-library'
import {
  HistoryMessageItem,
  MessageCallAppointmentEnd,
} from '@wui/im'
import { computed, watch, ref, nextTick } from 'vue'

import { chatModel, useUnreadMessages } from '@/entities/messages'

import { useLocalization } from '@/shared/composables/localization'
import { downloadUrlsSequentially } from '@/shared/utils/download-files'
import { getFullURL, getPathNameFromURL } from '@/shared/utils/url'

import {
  getAppointmentEventDisplayName,
  getAppointmentTimeRangeText,
  getCallRecordEvents,
} from '../'

const props = defineProps<{
  event: HistoryMessageItem<MessageCallAppointmentEnd>,
  light: boolean
}>()

const emit = defineEmits<{
  (event: 'click', value: {
    e: MouseEvent | TouchEvent,
    message: string
  }): void
}>()

const root = ref()
const { t } = useLocalization()
const { findRoomMessages } = chatModel.useChatMessageStore()

const timeRange = computed(() => getAppointmentTimeRangeText(props.event))
const statusText = computed(() => t('chat.groupCallEnded'))
const message = computed(() => statusText.value)
const displayAppointmentName = computed(() => (
  getAppointmentEventDisplayName(props.event.item.appointmentName, t('common.withoutTitle'))
))

const callRecordEvents = computed(() => {
  return getCallRecordEvents(findRoomMessages(props.event.item.pServed), props.event.item.confId)
})

const downloadRecordsLabel = computed(() => {
  return callRecordEvents.value.length > 1
    ? t('chat.downloadCallRecords', { count: callRecordEvents.value.length })
    : t('chat.downloadCallRecord')
})

const onDownloadRecords = async () => {
  const items = callRecordEvents.value.map(recordEvent => {
    const url = recordEvent.item.callRecordUrl ?? ''
    const filename = url.split('/').at(-1)
    return filename
      ? { url: getFullURL(getPathNameFromURL(url)), filename }
      : { url: getFullURL(getPathNameFromURL(url)) }
  })

  await downloadUrlsSequentially(items)
}

const onDetailsClick = (e: MouseEvent) => {
  emit('click', {
    e,
    message: displayAppointmentName.value ?? message.value,
  })
}

watch(() => props.light, async (v) => {
  if (v && root.value) {
    await nextTick()
    const detailsBtn = root.value.querySelector('[data-test="appointment-details-btn"]') as HTMLElement | null
    detailsBtn?.click()
  }
})

const { isUnread } = useUnreadMessages(props.event)
</script>
